/**
 * InvestorPortalsPage - Manage different portal presets (team, investors, network, etc.)
 * Features: Configure visible fields, sync settings, and access control per portal type
 */
import { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users,
  TrendingUp,
  Globe,
  Briefcase,
  Lock,
  Plus,
  RefreshCw,
  Eye,
  Edit2,
  Trash2,
  Shield,
  Settings,
  Loader2,
  ChevronRight,
  Check,
  X,
  Grid,
  Zap,
  Database
} from "lucide-react";

// Icon mapping for portal types
const ICON_MAP = {
  users: Users,
  "trending-up": TrendingUp,
  globe: Globe,
  briefcase: Briefcase,
  world: Globe,
  grid: Grid,
  lock: Lock
};

// Color classes for portal types
const COLOR_CLASSES = {
  violet: "bg-violet-100 text-violet-800 border-violet-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  gray: "bg-gray-100 text-gray-800 border-gray-200",
  rose: "bg-rose-100 text-rose-800 border-rose-200"
};

// Field categories for configuration
const FIELD_CATEGORIES = {
  profile: {
    label: "Profile Fields",
    fields: [
      { key: "full_name", label: "Full Name" },
      { key: "display_name", label: "Display Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "role", label: "Role/Title" },
      { key: "team", label: "Team" },
      { key: "start_date", label: "Start Date" },
      { key: "bio", label: "Bio" },
      { key: "public_bio", label: "Public Bio" },
      { key: "client_bio", label: "Client Bio" },
      { key: "skills", label: "Skills" },
      { key: "projects", label: "Projects" },
      { key: "social_links", label: "Social Links" }
    ]
  },
  company: {
    label: "Company Data",
    fields: [
      { key: "metrics", label: "All Metrics" },
      { key: "key_metrics", label: "Key Metrics Only" },
      { key: "public_metrics", label: "Public Metrics" },
      { key: "goals", label: "Goals" },
      { key: "roadmap", label: "Full Roadmap" },
      { key: "roadmap_highlights", label: "Roadmap Highlights" },
      { key: "finances", label: "Financial Data" },
      { key: "funding_status", label: "Funding Status" },
      { key: "all_members", label: "All Team Members" },
      { key: "team_highlights", label: "Team Highlights" },
      { key: "team_overview", label: "Team Overview" },
      { key: "public_team", label: "Public Team" },
      { key: "partnerships", label: "Partnerships" },
      { key: "services", label: "Services" },
      { key: "case_studies", label: "Case Studies" },
      { key: "testimonials", label: "Testimonials" },
      { key: "mission", label: "Mission" },
      { key: "press", label: "Press Coverage" },
      { key: "growth_charts", label: "Growth Charts" }
    ]
  },
  events: {
    label: "Events Access",
    fields: [
      { key: "all_events", label: "All Events" },
      { key: "public_events", label: "Public Events" },
      { key: "client_events", label: "Client Events" },
      { key: "investor_updates", label: "Investor Events" },
      { key: "networking_events", label: "Networking Events" },
      { key: "workshops", label: "Workshops" },
      { key: "attendance", label: "Attendance Data" },
      { key: "hosting", label: "Hosting Info" }
    ]
  },
  communications: {
    label: "Communications",
    fields: [
      { key: "internal_updates", label: "Internal Updates" },
      { key: "team_channels", label: "Team Channels" },
      { key: "meeting_notes", label: "Meeting Notes" },
      { key: "quarterly_reports", label: "Quarterly Reports" },
      { key: "investor_updates", label: "Investor Updates" },
      { key: "press_releases", label: "Press Releases" },
      { key: "newsletters", label: "Newsletters" },
      { key: "public_updates", label: "Public Updates" },
      { key: "product_updates", label: "Product Updates" },
      { key: "support_resources", label: "Support Resources" },
      { key: "blog", label: "Blog" }
    ]
  }
};

export default function InvestorPortalsPage() {
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  
  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newPreset, setNewPreset] = useState({
    name: "",
    description: "",
    icon: "grid",
    color: "gray",
    visible_fields: {},
    sync_settings: {},
    access_level: "custom"
  });
  
  useEffect(() => {
    fetchPresets();
    fetchDashboard();
  }, []);
  
  const fetchPresets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/portals/presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPresets(data.presets || []);
      }
    } catch (err) {
      console.error("Failed to fetch presets:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/portals/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    }
  };
  
  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset);
    setEditData({ ...preset });
    setEditMode(false);
    setActiveTab("fields");
  };
  
  const handleSaveChanges = async () => {
    if (!editData || !selectedPreset) return;
    
    // Check if it's a default preset
    if (selectedPreset.preset_id?.startsWith("preset_") && !selectedPreset.is_custom) {
      toast.error("Default presets cannot be modified. Create a custom preset instead.");
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/portals/presets/${selectedPreset.preset_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      
      if (res.ok) {
        toast.success("Portal preset updated!");
        setEditMode(false);
        fetchPresets();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update preset");
      }
    } catch (err) {
      toast.error("Failed to save changes");
    }
  };
  
  const handleCreatePreset = async () => {
    if (!newPreset.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/portals/presets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPreset)
      });
      
      if (res.ok) {
        toast.success("Portal preset created!");
        setShowCreate(false);
        setNewPreset({
          name: "",
          description: "",
          icon: "grid",
          color: "gray",
          visible_fields: {},
          sync_settings: {},
          access_level: "custom"
        });
        fetchPresets();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create preset");
      }
    } catch (err) {
      toast.error("Failed to create preset");
    }
  };
  
  const handleDeletePreset = async (presetId) => {
    if (!confirm("Delete this custom preset?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/portals/presets/${presetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Preset deleted");
        if (selectedPreset?.preset_id === presetId) {
          setSelectedPreset(null);
        }
        fetchPresets();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete preset");
      }
    } catch (err) {
      toast.error("Failed to delete preset");
    }
  };
  
  const toggleField = (category, field) => {
    if (!editMode) return;
    
    setEditData(prev => {
      const currentFields = prev.visible_fields?.[category] || [];
      const newFields = currentFields.includes(field)
        ? currentFields.filter(f => f !== field)
        : [...currentFields, field];
      
      return {
        ...prev,
        visible_fields: {
          ...prev.visible_fields,
          [category]: newFields
        }
      };
    });
  };
  
  const isFieldEnabled = (category, field) => {
    const data = editMode ? editData : selectedPreset;
    return data?.visible_fields?.[category]?.includes(field) ?? false;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-600" />
              Support Portals
            </h1>
            <p className="text-gray-600">Configure portal views for investors, team, network, and support access</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchPresets(); fetchDashboard(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Preset
            </Button>
          </div>
        </div>
        
        {/* Main Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Preset List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Portal Presets</CardTitle>
                <CardDescription>Select a preset to configure</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-3 space-y-2">
                    {/* Default Presets Section */}
                    <p className="text-xs font-medium text-gray-500 px-2 pt-2">Default Presets</p>
                    {presets.filter(p => !p.is_custom).map((preset) => {
                      const IconComponent = ICON_MAP[preset.icon] || Grid;
                      const colorClass = COLOR_CLASSES[preset.color] || COLOR_CLASSES.gray;
                      const isSelected = selectedPreset?.preset_id === preset.preset_id;
                      const accessCount = dashboard?.access_stats?.[preset.preset_id] || 0;
                      
                      return (
                        <div
                          key={preset.preset_id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => handleSelectPreset(preset)}
                          data-testid={`preset-${preset.preset_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{preset.name}</p>
                              <p className="text-xs text-gray-500 truncate">{preset.description}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline" className="text-[10px]">
                                {accessCount} users
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Custom Presets Section */}
                    {presets.filter(p => p.is_custom).length > 0 && (
                      <>
                        <p className="text-xs font-medium text-gray-500 px-2 pt-4">Custom Presets</p>
                        {presets.filter(p => p.is_custom).map((preset) => {
                          const IconComponent = ICON_MAP[preset.icon] || Grid;
                          const colorClass = COLOR_CLASSES[preset.color] || COLOR_CLASSES.gray;
                          const isSelected = selectedPreset?.preset_id === preset.preset_id;
                          
                          return (
                            <div
                              key={preset.preset_id}
                              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                              onClick={() => handleSelectPreset(preset)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center`}>
                                  <IconComponent className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm">{preset.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{preset.description}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.preset_id); }}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Panel - Detail/Editor */}
          <div className="lg:col-span-2">
            {selectedPreset ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const IconComponent = ICON_MAP[selectedPreset.icon] || Grid;
                        const colorClass = COLOR_CLASSES[selectedPreset.color] || COLOR_CLASSES.gray;
                        return (
                          <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center`}>
                            <IconComponent className="w-6 h-6" />
                          </div>
                        );
                      })()}
                      <div>
                        <CardTitle>{selectedPreset.name}</CardTitle>
                        <CardDescription>{selectedPreset.description}</CardDescription>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {editMode ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditData({ ...selectedPreset }); }}>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveChanges}>
                            <Check className="w-4 h-4 mr-1" />
                            Save Changes
                          </Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4">
                      <TabsTrigger value="fields">Visible Fields</TabsTrigger>
                      <TabsTrigger value="sync">Sync Settings</TabsTrigger>
                      <TabsTrigger value="access">Access Control</TabsTrigger>
                    </TabsList>
                    
                    {/* Visible Fields Tab */}
                    <TabsContent value="fields">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-6">
                          {Object.entries(FIELD_CATEGORIES).map(([categoryKey, category]) => (
                            <div key={categoryKey} className="border rounded-lg p-4">
                              <h4 className="font-medium text-sm mb-3">{category.label}</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {category.fields.map((field) => {
                                  const isEnabled = isFieldEnabled(categoryKey, field.key);
                                  
                                  return (
                                    <div
                                      key={field.key}
                                      className={`flex items-center gap-2 p-2 rounded border transition-colors ${
                                        editMode ? 'cursor-pointer hover:bg-gray-50' : ''
                                      } ${isEnabled ? 'bg-emerald-50 border-emerald-200' : 'border-gray-200'}`}
                                      onClick={() => toggleField(categoryKey, field.key)}
                                    >
                                      {editMode ? (
                                        <Checkbox checked={isEnabled} />
                                      ) : (
                                        <div className={`w-4 h-4 rounded-full ${isEnabled ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                                          {isEnabled && <Check className="w-4 h-4 text-white" />}
                                        </div>
                                      )}
                                      <span className="text-sm">{field.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Sync Settings Tab */}
                    <TabsContent value="sync">
                      <div className="space-y-4">
                        <h4 className="font-medium text-sm text-gray-700">Data Synchronization</h4>
                        <p className="text-xs text-gray-500">Configure how data syncs for this portal</p>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Sync to CRM */}
                          <div className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-2">
                              <Database className="w-4 h-4 text-gray-500" />
                              <Label className="text-sm">Sync to CRM</Label>
                            </div>
                            {editMode ? (
                              <Switch
                                checked={editData?.sync_settings?.sync_to_crm ?? false}
                                onCheckedChange={(checked) => setEditData(prev => ({
                                  ...prev,
                                  sync_settings: { ...prev.sync_settings, sync_to_crm: checked }
                                }))}
                              />
                            ) : (
                              <Badge variant={selectedPreset.sync_settings?.sync_to_crm ? "default" : "outline"}>
                                {selectedPreset.sync_settings?.sync_to_crm ? "On" : "Off"}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Sync to Beehiiv */}
                          <div className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4 text-gray-500" />
                              <Label className="text-sm">Sync to Beehiiv</Label>
                            </div>
                            {editMode ? (
                              <Switch
                                checked={editData?.sync_settings?.sync_to_beehiiv ?? false}
                                onCheckedChange={(checked) => setEditData(prev => ({
                                  ...prev,
                                  sync_settings: { ...prev.sync_settings, sync_to_beehiiv: checked }
                                }))}
                              />
                            ) : (
                              <Badge variant={selectedPreset.sync_settings?.sync_to_beehiiv ? "default" : "outline"}>
                                {selectedPreset.sync_settings?.sync_to_beehiiv ? "On" : "Off"}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Real-time Sync */}
                          <div className="flex items-center justify-between p-3 border rounded-lg col-span-2">
                            <div>
                              <Label className="text-sm">Real-time Sync</Label>
                              <p className="text-xs text-gray-500">Sync changes immediately</p>
                            </div>
                            {editMode ? (
                              <Switch
                                checked={editData?.sync_settings?.real_time ?? false}
                                onCheckedChange={(checked) => setEditData(prev => ({
                                  ...prev,
                                  sync_settings: { ...prev.sync_settings, real_time: checked }
                                }))}
                              />
                            ) : (
                              <Badge variant={selectedPreset.sync_settings?.real_time ? "default" : "outline"}>
                                {selectedPreset.sync_settings?.real_time ? "Enabled" : "Scheduled"}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Update Frequency */}
                        <div className="border rounded-lg p-4">
                          <Label className="text-sm mb-2 block">Update Frequency</Label>
                          {editMode ? (
                            <Select
                              value={editData?.sync_settings?.update_frequency || "realtime"}
                              onValueChange={(v) => setEditData(prev => ({
                                ...prev,
                                sync_settings: { ...prev.sync_settings, update_frequency: v }
                              }))}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="realtime">Real-time</SelectItem>
                                <SelectItem value="hourly">Hourly</SelectItem>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline" className="capitalize">
                              {selectedPreset.sync_settings?.update_frequency || "Real-time"}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Access Control Tab */}
                    <TabsContent value="access">
                      <div className="space-y-4">
                        <h4 className="font-medium text-sm text-gray-700">Access Level</h4>
                        
                        <div className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <Label className="text-sm">Access Type</Label>
                              <p className="text-xs text-gray-500">Who can view this portal</p>
                            </div>
                            {editMode ? (
                              <Select
                                value={editData?.access_level || "custom"}
                                onValueChange={(v) => setEditData(prev => ({ ...prev, access_level: v }))}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="internal">Internal Only</SelectItem>
                                  <SelectItem value="investor">Investors</SelectItem>
                                  <SelectItem value="network">Network</SelectItem>
                                  <SelectItem value="client">Clients</SelectItem>
                                  <SelectItem value="public">Public</SelectItem>
                                  <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="outline" className="capitalize">
                                {selectedPreset.access_level || "Custom"}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Users className="w-4 h-4" />
                            <span>{dashboard?.access_stats?.[selectedPreset.preset_id] || 0} users have access</span>
                          </div>
                        </div>
                        
                        {/* Access Management Note */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-800">
                            <strong>Note:</strong> To manage individual user access, go to People → User Profile → Portal Access
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-700">Select a Portal Preset</h3>
                  <p className="text-sm text-gray-500 mt-1">Choose a preset from the list to view and configure</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {/* Create Preset Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Portal Preset</DialogTitle>
            <DialogDescription>Create a custom portal configuration</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Preset Name *</Label>
              <Input
                value={newPreset.name}
                onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                placeholder="e.g., Advisory Board"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newPreset.description}
                onChange={(e) => setNewPreset({ ...newPreset, description: e.target.value })}
                placeholder="What is this portal for?"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Icon</Label>
                <Select
                  value={newPreset.icon}
                  onValueChange={(v) => setNewPreset({ ...newPreset, icon: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="users">Users</SelectItem>
                    <SelectItem value="trending-up">Trending</SelectItem>
                    <SelectItem value="globe">Globe</SelectItem>
                    <SelectItem value="briefcase">Briefcase</SelectItem>
                    <SelectItem value="grid">Grid</SelectItem>
                    <SelectItem value="lock">Lock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Color</Label>
                <Select
                  value={newPreset.color}
                  onValueChange={(v) => setNewPreset({ ...newPreset, color: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="violet">Violet</SelectItem>
                    <SelectItem value="emerald">Emerald</SelectItem>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="amber">Amber</SelectItem>
                    <SelectItem value="rose">Rose</SelectItem>
                    <SelectItem value="gray">Gray</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Access Level</Label>
              <Select
                value={newPreset.access_level}
                onValueChange={(v) => setNewPreset({ ...newPreset, access_level: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Only</SelectItem>
                  <SelectItem value="investor">Investors</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="client">Clients</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreatePreset}>Create Preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
